import { useEffect, useMemo, useState } from "react";
import {
  fetchCatalogSnapshotMeta,
  fetchStorefrontHome,
  getFrontendBuildId,
  getApiBaseForDebug,
  trackMiniAppEvent,
} from "../../api/client.js";
import type { StorefrontHomeProductItem, StorefrontHomeSection } from "../../api/types.js";
import { readCatalogDegradedState } from "../../lib/catalog-degraded.js";
import {
  readCatalogSnapshotStatus,
  type CatalogSnapshotStatus,
} from "../../lib/catalog-snapshot-status.js";
import { BUSINESS_HOME_TILES } from "../../lib/business-home-tiles.js";
import { clearMiniAppStoreUiCache } from "../../lib/store-cache-reset.js";
import { AppButton } from "../ui/AppButton.js";
import { ProductCardSkeleton } from "../ui/Skeleton.js";
import { SearchInput } from "../catalog/SearchInput.js";
import { DegradedCatalogBanner } from "../catalog/DegradedCatalogBanner.js";
import { CatalogSnapshotStatusBanner } from "../catalog/CatalogSnapshotStatusBanner.js";
import { messages } from "../../i18n/messages.js";
import { formatCustomerPayablePrice } from "../../lib/format.js";

export interface CatalogFilter {
  category?: string | null;
  collection?: string | null;
}

interface HomeViewProps {
  cartCount: number;
  onOpenCatalog: (filter?: { category?: string | null; collection?: string | null }) => void;
  onOpenAllCategories: () => void;
  onOpenCart: () => void;
  onAddToCart: (productId: string) => Promise<void>;
  cartMutating?: boolean;
  /** When true, block add-to-cart from home (commerce gate) */
  catalogReadOnly?: boolean;
  onCatalogCommerceChange?: (state: {
    cartDisabled: boolean;
    checkoutDisabled: boolean;
    degraded: boolean;
    messageUk: string;
  }) => void;
}

export function HomeView({
  cartCount,
  onOpenCatalog,
  onOpenAllCategories,
  onOpenCart,
  onAddToCart,
  cartMutating = false,
  catalogReadOnly = false,
  onCatalogCommerceChange,
}: HomeViewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<StorefrontHomeSection[]>([]);
  const [degradedMessage, setDegradedMessage] = useState<string | null>(null);
  const [cartDisabledLocal, setCartDisabledLocal] = useState(false);
  const [snapshotStatus, setSnapshotStatus] = useState<CatalogSnapshotStatus | null>(null);
  const [tileCounts, setTileCounts] = useState<Record<string, number>>({});
  const buildId = getFrontendBuildId() || "no-build-id";

  useEffect(() => {
    const startParam =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("tgWebAppStartParam") ||
          new URLSearchParams(window.location.search).get("start_param") ||
          new URLSearchParams(window.location.search).get("build")
        : null;
    void trackMiniAppEvent("miniapp_opened", {
      surface: "home",
      build_id: buildId,
      start_param: startParam,
    });
    void trackMiniAppEvent("miniapp_home_viewed", { surface: "home", build_id: buildId });
  }, [buildId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [home, meta] = await Promise.all([
          fetchStorefrontHome(),
          fetchCatalogSnapshotMeta().catch(() => null),
        ]);
        if (!cancelled) {
          const operational = home.sections.filter((section) => section.operational);
          setSections(operational);
          const counts: Record<string, number> = {};
          for (const section of operational) {
            if (section.kind !== "categories" || !section.categories) continue;
            for (const cat of section.categories) {
              counts[cat.id] = cat.product_count;
            }
          }
          setTileCounts(counts);
          const degraded = readCatalogDegradedState(home);
          setDegradedMessage(degraded.degraded ? degraded.messageUk : null);
          setCartDisabledLocal(degraded.cartDisabled);
          onCatalogCommerceChange?.({
            cartDisabled: degraded.cartDisabled,
            checkoutDisabled: degraded.checkoutDisabled,
            degraded: degraded.degraded,
            messageUk: degraded.messageUk,
          });
          setSnapshotStatus(meta ? readCatalogSnapshotStatus(meta) : null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : messages.home.loadFailed);
          setSections([]);
          setDegradedMessage(null);
          setCartDisabledLocal(false);
          setSnapshotStatus(null);
          onCatalogCommerceChange?.({
            cartDisabled: false,
            checkoutDisabled: false,
            degraded: false,
            messageUk: "",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onCatalogCommerceChange]);

  const readOnly = catalogReadOnly || cartDisabledLocal;
  const productSections = useMemo(
    () => sections.filter((s) => s.kind === "products"),
    [sections],
  );

  function handleClearStoreCache() {
    clearMiniAppStoreUiCache();
    void trackMiniAppEvent("store_cache_cleared", { surface: "home", build_id: buildId });
    window.location.reload();
  }

  return (
    <section className="flex flex-1 flex-col gap-5 px-4 pt-4 pb-28">
      <div className="rounded-[14px] border-2 border-orange-500 bg-orange-100 px-3 py-3 text-sm leading-snug text-orange-950">
        <p className="text-base font-bold tracking-tight">BUILD {buildId}</p>
        <p className="mt-1 text-[11px] opacity-80">
          api {getApiBaseForDebug() || "(same-origin)"}
          {snapshotStatus?.snapshotId ? ` · snap ${snapshotStatus.snapshotId}` : ""}
        </p>
        <p className="mt-1 text-[11px] font-medium text-orange-800">
          Якщо не бачите цей BUILD — закрийте Mini App і відкрийте знов через «Відкрити магазин».
        </p>
      </div>

      {snapshotStatus ? <CatalogSnapshotStatusBanner status={snapshotStatus} /> : null}
      {degradedMessage ? <DegradedCatalogBanner message={degradedMessage} /> : null}

      <div className="overflow-hidden rounded-[18px] bg-gradient-to-br from-orange-50 to-amber-100 p-4 dark:from-orange-950/40 dark:to-amber-950/20">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-800">ASIASTYLE</p>
        <h2 className="mt-1 text-xl font-semibold leading-tight">{messages.home.heroTitle}</h2>
        <p className="mt-2 text-sm opacity-80">{messages.home.heroSubtitle}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <AppButton className="w-full" onClick={() => onOpenCatalog()}>
            {messages.home.browseCatalog}
          </AppButton>
          <AppButton variant="secondary" className="w-full" onClick={onOpenCart}>
            🛒 {messages.nav.cart}
            {cartCount > 0 ? ` (${cartCount})` : ""}
          </AppButton>
        </div>
        <button
          type="button"
          onClick={handleClearStoreCache}
          className="mt-3 w-full text-left text-[11px] underline opacity-70"
        >
          Скинути кеш магазину
        </button>
      </div>

      <SearchInput value="" onChange={() => onOpenCatalog()} readOnly onFocus={() => onOpenCatalog()} />

      <div>
        <h3 className="mb-3 text-sm font-semibold">{messages.home.categoriesTitle}</h3>
        <div className="grid grid-cols-2 gap-2.5">
          {BUSINESS_HOME_TILES.map((tile) => (
            <button
              key={tile.id}
              type="button"
              onClick={() => {
                void trackMiniAppEvent("miniapp_category_selected", {
                  category_key: tile.id,
                  surface: "home_tile_v2",
                  build_id: buildId,
                });
                onOpenCatalog({ category: tile.id });
              }}
              className="flex min-h-[4.5rem] flex-col items-start justify-between rounded-[16px] border border-black/8 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3 text-left"
            >
              <span className="text-sm font-medium leading-snug">{tile.label}</span>
              {typeof tileCounts[tile.id] === "number" ? (
                <span className="mt-2 text-[11px] opacity-55">{tileCounts[tile.id]}</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="rounded-[16px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {!loading && !error
        ? productSections.map((section) => (
            <HomeSectionBlock
              key={section.key}
              section={section}
              onOpenCatalog={onOpenCatalog}
              onOpenAllCategories={onOpenAllCategories}
              onAddToCart={onAddToCart}
              cartMutating={cartMutating}
              catalogReadOnly={readOnly}
            />
          ))
        : null}

      <AppButton variant="secondary" className="w-full" onClick={onOpenAllCategories}>
        {messages.catalog.allCategories}
      </AppButton>
    </section>
  );
}

function HomeSectionBlock({
  section,
  onOpenCatalog,
  onOpenAllCategories,
  onAddToCart,
  cartMutating,
  catalogReadOnly = false,
}: {
  section: StorefrontHomeSection;
  onOpenCatalog: HomeViewProps["onOpenCatalog"];
  onOpenAllCategories: () => void;
  onAddToCart: (productId: string) => Promise<void>;
  cartMutating: boolean;
  catalogReadOnly?: boolean;
}) {
  useEffect(() => {
    void trackMiniAppEvent("miniapp_merchandising_section_viewed", {
      section_key: section.key,
      source: section.source,
    });
  }, [section.key, section.source]);

  if (section.kind === "categories" && section.categories?.length) {
    const isTaxonomyTiles = section.key === "business_taxonomy_tiles";
    if (isTaxonomyTiles) {
      return (
        <div>
          <h3 className="mb-3 text-sm font-semibold">{section.title}</h3>
          <div className="grid grid-cols-2 gap-2.5">
            {section.categories.map((tile) => (
              <button
                key={tile.id}
                type="button"
                onClick={() => {
                  void trackMiniAppEvent("miniapp_category_selected", {
                    category_key: tile.id,
                    surface: "home_tile",
                  });
                  onOpenCatalog({ category: tile.id });
                }}
                className="flex min-h-[4.5rem] flex-col items-start justify-between rounded-[16px] border border-black/8 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3 text-left"
              >
                <span className="text-sm font-medium leading-snug">{tile.label}</span>
                {typeof tile.product_count === "number" ? (
                  <span className="mt-2 text-[11px] opacity-55">{tile.product_count}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div>
        <h3 className="mb-2 text-sm font-semibold">{section.title}</h3>
        <div className="-mx-1 flex flex-wrap gap-2 px-1">
          {section.categories.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => {
                void trackMiniAppEvent("miniapp_category_selected", {
                  category_key: chip.id,
                  surface: "home",
                });
                onOpenCatalog({ category: chip.id });
              }}
              className="shrink-0 rounded-full border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 py-2 text-xs font-medium"
            >
              {chip.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              void trackMiniAppEvent("miniapp_category_directory_opened", { surface: "home" });
              onOpenAllCategories();
            }}
            className="shrink-0 rounded-full border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 py-2 text-xs font-medium"
          >
            {messages.catalog.allCategories}
          </button>
        </div>
      </div>
    );
  }

  if (section.kind === "service_card" && section.service) {
    return (
      <div className="rounded-[16px] border border-orange-200 bg-orange-50 p-4">
        <h3 className="text-sm font-semibold text-orange-900">{section.service.title}</h3>
        {section.service.description ? (
          <p className="mt-2 text-sm opacity-80">{section.service.description}</p>
        ) : null}
        {section.service.route ? (
          <AppButton
            className="mt-3 w-full"
            variant="secondary"
            onClick={() => onOpenCatalog()}
          >
            {section.service.title}
          </AppButton>
        ) : null}
      </div>
    );
  }

  if (section.kind === "products" && section.products?.length) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-semibold">{section.title}</h3>
        <div className="grid grid-cols-2 gap-3">
          {section.products.map((product) => (
            <HomeProductTile
              key={product.product_id}
              product={product}
              onOpen={() => {
                void trackMiniAppEvent("miniapp_recommendation_clicked", {
                  product_id: product.product_id,
                  section_key: section.key,
                });
                onOpenCatalog();
              }}
              onAdd={() => void onAddToCart(product.product_id)}
              cartMutating={cartMutating}
              catalogReadOnly={catalogReadOnly}
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function HomeProductTile({
  product,
  onOpen,
  onAdd,
  cartMutating,
  catalogReadOnly = false,
}: {
  product: StorefrontHomeProductItem;
  onOpen: () => void;
  onAdd: () => void;
  cartMutating: boolean;
  catalogReadOnly?: boolean;
}) {
  const canAdd =
    !catalogReadOnly &&
    product.purchasable !== false &&
    (product.availability === "in_stock" ||
      product.availability === "low_stock" ||
      (product.purchasable === true && product.availability == null));

  return (
    <article className="flex flex-col overflow-hidden rounded-[16px] border border-black/5 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)]">
      <button type="button" onClick={onOpen} className="flex flex-1 flex-col p-2.5 text-left">
        {product.image_url ? (
          <img src={product.image_url} alt="" className="mb-2 aspect-square w-full rounded-[12px] object-cover" />
        ) : null}
        <p className="line-clamp-2 text-sm font-medium">{product.title}</p>
        <p className="mt-1 text-sm font-semibold">
          {formatCustomerPayablePrice({ payable_price_uah: product.payable_price_uah })}
        </p>
        {product.stock_display ? (
          <p className="mt-1 text-[11px] opacity-60">{product.stock_display}</p>
        ) : null}
      </button>
      {canAdd ? (
        <button
          type="button"
          disabled={cartMutating}
          onClick={onAdd}
          className="mx-2.5 mb-2.5 min-h-11 rounded-[14px] bg-[var(--tg-theme-button-color,#3390ec)] text-sm font-medium text-[var(--tg-theme-button-text-color,#ffffff)] disabled:opacity-50"
        >
          +
        </button>
      ) : null}
    </article>
  );
}
